import { Outlet } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Sidebar, type SidebarItem } from '../../components/Sidebar'
import { AccountMenu } from '../../components/AccountMenu'
import { AppShellLayout } from '../../components/AppShellLayout'
import { AlertCenter } from '../../components/AlertCenter'
import { workspaceSidebarItems } from '../../lib/workspaceSidebar'
import { useTaskBadge } from '../staff-tasks/useTaskBadge'
import { staffTasksI18n } from '../../i18n/shared/staff-tasks'
import { walletSidebarItems } from '../../lib/walletSidebar'
import {
  SUPPLY_PERSONAS,
  useSupplyPersonaStore,
  type SupplyPersona,
} from '../../store/role'
import {
  hasCapability,
  resolveCurrentUser,
} from '../../lib/user-permissions'
import {
  FilterBar,
  InfoCard,
  MetricCard,
  AnnouncementBanner,
  QuickActionCard,
  BodyCard,
  SectionHeader,
} from '../../components/DashboardCards'
import { toast } from '../../lib/toast'
import { USE_REAL_API } from '../../lib/api'
import { fetchMe } from '../../lib/auth'
import { useT } from '../../i18n'
import { supplyShellI18n } from '../../i18n/supply/shell'
import { drivePersonalI18n } from '../../i18n/shared/drive-personal'
import { messagingI18n } from '../../i18n/messaging'
import { useMessagingCapabilities } from '../shared/messaging/useMessagingCapabilities'
import { TenantMessagingLauncher } from '../shared/messaging/TenantMessagingLauncher'
import { useMessagingUnread } from '../shared/messaging/useMessagingUnread'
import { useMessagingRealtime } from '../shared/messaging/useMessagingRealtime'

export function SupplyShell() {
  const persona = useSupplyPersonaStore((s) => s.supplyPersona)
  const setPersona = useSupplyPersonaStore((s) => s.setSupplyPersona)
  const info = SUPPLY_PERSONAS[persona]
  const t = useT(supplyShellI18n)
  const td = useT(drivePersonalI18n)
  const tm = useT(messagingI18n)
  const tTasks = useT(staffTasksI18n)
  const taskBadge = useTaskBadge()
  const messagingCapabilities = useMessagingCapabilities()
  const messagingUnread = useMessagingUnread(
    'supply',
    messagingCapabilities.can_view_inbox,
  )
  useMessagingRealtime('supply', messagingCapabilities.can_view_inbox)

  // Real-engine: the shell layout (SO2 vs SO3) + scan capability derive from the
  // JWT-resolved membership, NOT the maket persona switcher — otherwise a
  // logged-in SO2 vendor would see the SO3 menu. Maket default is unchanged
  // (query disabled, mock persona drives the UI).
  const realMe = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: fetchMe,
    enabled: USE_REAL_API,
    staleTime: 5 * 60 * 1000,
  })
  const realOrgType = realMe.data?.active_membership?.org_type as
    | 'SO1'
    | 'SO2'
    | 'SO3'
    | 'A2'
    | undefined
  const effectiveOrgType = USE_REAL_API ? (realOrgType ?? 'SO2') : info.org_type

  // T1.11 shipping-v1: Print Queue sidebar entry conditional on
  // ship.shipment.scan capability. Mehmet/Hakan SO2 + Aybars SO3 hidden;
  // Emin warehouse_operator persona visible (A-32 §A7).
  const me = resolveCurrentUser({ role: 'supply', supplyPersona: persona })
  const canScan = USE_REAL_API
    ? (realMe.data?.capabilities ?? []).includes('ship.shipment.scan')
    : hasCapability(me.user_id, 'ship.shipment.scan')
  const printQueueItem: SidebarItem = {
    to: '/supply/print-queue',
    label: t.items.todaysPrintQueue,
    icon: 'printing',
  }

  const baseItems: SidebarItem[] =
    effectiveOrgType === 'SO3'
      ? [
          { to: '/supply', label: t.items.overview, icon: 'navOverview' },
          { to: '/supply/po-inbox', label: t.items.poInbox, icon: 'inbox' },
          {
            to: '/supply/my-updates',
            label: t.items.mySubmittedUpdates,
            icon: 'write',
          },
          {
            to: '/supply/my-updates/new',
            label: t.items.submitNewUpdate,
            icon: 'newDocument',
          },
          { to: '/supply/activity', label: t.items.activity, icon: 'activity' },
          ...walletSidebarItems('/supply'),
          ...workspaceSidebarItems('/supply', { label: tTasks.navLabel, badge: taskBadge }),
        ]
      : [
          { to: '/supply', label: t.items.overview, icon: 'navOverview' },
          { to: '/supply/po-inbox', label: t.items.poInbox, icon: 'inbox' },
          { to: '/supply/work-orders', label: t.items.workOrders, icon: 'production' },
          { to: '/supply/prepare-pack', label: t.items.preparePack, icon: 'package' },
          { to: '/supply/samples', label: t.items.samples, icon: 'experiment' },
          ...(canScan ? [printQueueItem] : []),
          { to: '/supply/activity', label: t.items.activity, icon: 'activity' },
          ...walletSidebarItems('/supply'),
          ...workspaceSidebarItems('/supply', { label: tTasks.navLabel, badge: taskBadge }),
        ]
  const items: SidebarItem[] = [
    ...baseItems.slice(0, 1),
    ...(messagingCapabilities.can_view_inbox
      ? [{
          to: '/supply/messages',
          label: tm.navigation.supply,
          icon: 'messages' as const,
          badge: messagingUnread.unreadMessages,
        }]
      : []),
    ...baseItems.slice(1),
    { to: '/supply/drive', label: td.pageTitle, icon: 'cloud' },
    { to: '/supply/settings-divider', label: '', isHeader: true },
    { to: '/supply/settings', label: t.items.settings, icon: 'navSettings' },
    { to: '/supply/resources', label: t.items.resources, icon: 'learning' },
    { to: '/supply/my-permissions', label: t.items.myPermissions, icon: 'security' },
  ]

  return (
    <>
      <AppShellLayout
      sidebar={
        <Sidebar
          title={`${t.titlePrefix} / ${effectiveOrgType}`}
          layout="aligned"
          items={items}
          footer={<AccountMenu shellBase="/supply" fallbackName={t.fallbackName} />}
        />
      }
      contextBar={
        <div className="flex items-center gap-2 border-b border-[var(--color-border)] bg-[var(--color-raised)] px-3 py-1.5 text-xs safe-left safe-right sm:gap-3 sm:px-6 sm:text-sm">
          <span className="hidden text-[var(--color-text-muted)] sm:inline">{t.personaLabel}</span>
          {USE_REAL_API ? (
            // Real-engine: identity is the logged-in JWT user — no persona switch.
            <span className="rounded-md border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-2 py-1 text-xs text-[var(--color-text)] sm:text-sm">
              {realMe.data?.active_membership?.persona_label ??
                realMe.data?.user.display_name ??
                effectiveOrgType}{' '}
              ({effectiveOrgType})
            </span>
          ) : (
            <>
              <select
                value={persona}
                onChange={(e) => setPersona(e.target.value as SupplyPersona)}
                className="max-w-[180px] rounded-md border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-2 py-1.5 text-xs text-[var(--color-text)] focus:border-[var(--color-focus)] focus:outline-none focus-visible:shadow-[var(--shadow-focus)] sm:max-w-none sm:py-1 sm:text-sm"
                aria-label={t.switchPersona}
              >
                {Object.values(SUPPLY_PERSONAS).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.org_name} ({p.org_type})
                  </option>
                ))}
              </select>
              <span className="hidden truncate text-[var(--color-text-muted)] lg:inline">— {info.description}</span>
            </>
          )}
        </div>
      }
    >
      <Outlet />
      </AppShellLayout>
      <TenantMessagingLauncher surface="supply" />
    </>
  )
}

export function SupplyHome() {
  const persona = useSupplyPersonaStore((s) => s.supplyPersona)
  const info = SUPPLY_PERSONAS[persona]
  const t = useT(supplyShellI18n)
  const h = t.home

  return (
    <div className="max-w-6xl space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-[var(--color-text)]">
          {h.pageTitle}
        </h1>
        <p className="mt-1 text-sm text-[var(--color-text-muted)]">
          {h.pageDescriptionPrefix}
          <span className="font-medium text-[var(--color-text)]">{info.org_name}</span>{' '}
          ({info.org_type}){h.pageDescriptionInfix}
          {info.description}
          {h.pageDescriptionSuffix}
        </p>
      </header>

      {/* a) Filter bar */}
      <FilterBar
        liveLabel={h.liveLabel}
        rightSlot={<InfoCard label={h.nextPickupLabel} value="14:30 EST" />}
      />

      {/* b) Metric cards row */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
        <MetricCard
          label={h.metrics.activeWos}
          value="12"
          trend={{ direction: 'up', percent: 9 }}
          sparkline={[6, 7, 8, 9, 10, 11, 12]}
        />
        <MetricCard
          label={h.metrics.completedToday}
          value="4"
          trend={{ direction: 'up', percent: 25 }}
          sparkline={[1, 1, 2, 2, 3, 4, 4]}
        />
        <MetricCard
          label={h.metrics.pendingQc}
          value="3"
          trend={{ direction: 'down', percent: 14 }}
          sparkline={[5, 5, 4, 4, 3, 3, 3]}
        />
        <MetricCard
          label={h.metrics.onTimeRate}
          value="94%"
          trend={{ direction: 'up', percent: 3 }}
          sparkline={[88, 89, 90, 91, 92, 93, 94]}
        />
      </div>

      {/* c) Announcement banner */}
      <AnnouncementBanner
        eyebrow={h.announcementEyebrow}
        title={h.announcementTitle}
        body={h.announcementBody}
        ctaLabel={h.announcementCta}
        onCta={() =>
          toast.info(h.announcementTitle, {
            description: h.announcementToastDescription,
            audience: 'supply',
          })
        }
      />

      {/* d) Quick action cards */}
      <div>
        <SectionHeader title={h.quickActionsHeader} hint={h.quickActionsHint} />
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <QuickActionCard
            icon="production"
            label={h.quickActions.workOrdersToStart}
            onClick={() =>
              toast.info(h.quickActionsToastTitle, {
                description: h.quickActionsToastDescriptions.workOrderQueue,
                audience: 'supply',
              })
            }
          />
          <QuickActionCard
            icon="taskComplete"
            label={h.quickActions.qcPending}
            onClick={() =>
              toast.info(h.quickActionsToastTitle, {
                description: h.quickActionsToastDescriptions.qualityCheckQueue,
                audience: 'supply',
              })
            }
          />
          <QuickActionCard
            icon="warning"
            label={h.quickActions.qualityIssue}
            tone="warning"
            onClick={() =>
              toast.info(h.quickActionsToastTitle, {
                description: h.quickActionsToastDescriptions.qualityIncident,
                audience: 'supply',
              })
            }
          />
          <QuickActionCard
            icon="fulfillment"
            label={h.quickActions.shipmentReady}
            onClick={() =>
              toast.info(h.quickActionsToastTitle, {
                description: h.quickActionsToastDescriptions.readyToShip,
                audience: 'supply',
              })
            }
          />
        </div>
      </div>

      {/* e) Body content */}
      <section>
        <AlertCenter />
      </section>

      {info.org_type === 'SO3' && (
        <BodyCard title={h.so3BodyCardTitle}>
          <p className="text-sm leading-relaxed text-[var(--color-text-muted)]">{h.so3BodyCardBody}</p>
        </BodyCard>
      )}

      {info.org_type === 'SO2' && (
        <BodyCard title={h.so2BodyCardTitle}>
          <p className="text-sm leading-relaxed text-[var(--color-text-muted)]">
            {h.so2BodyCardParagraph1}
          </p>
          <p className="mt-2 text-sm leading-relaxed text-[var(--color-text-muted)]">
            <span className="font-medium text-[var(--color-text)]">
              {h.so2BodyCardParagraph2Prefix}
            </span>
            {h.so2BodyCardParagraph2Body}
          </p>
        </BodyCard>
      )}

      <div className="rounded-lg border border-dashed border-[var(--color-border-strong)] bg-[var(--color-surface)] p-4 text-xs text-[var(--color-text-muted)]">
        <strong className="text-[var(--color-text)]">{h.m1ScopeStrong}</strong>
        {h.m1ScopeBody}
      </div>
    </div>
  )
}
